import { httpClient } from './../httpClient';
import { apiErrorHandler } from './helpers';
import { useCustomPaginatedQuery } from 'hooks/reactQuery';

/** Normalize id args that may be a single id or an array (sync, export, etc.). */
const toIdArray = ids => (Array.isArray(ids) ? ids : [ids]);

export const useGetPaginatedExtAuthentications = query => {
  const getPaginatedExtAuthentications = async (key, query) => {
    return httpClient
      .get('/extAuthentication/paginated', { params: query })
      .then(res => res.data)
      .catch(apiErrorHandler);
  };

  return useCustomPaginatedQuery('extAuthentications', getPaginatedExtAuthentications, query);
};

export const getExtAuthenticationDetail = id => {
  return httpClient
    .get(`/extAuthentication/${id}/detail`)
    .then(response => response.data)
    .catch(apiErrorHandler);
};

export const useGetExtAuthenticationActivity = (extAuthenticationId, query, options = {}) => {
  const getExtAuthenticationActivity = async (key, queryToFetch) => {
    const data = await httpClient
      .get(`/extAuthentication/${key}/activity`, { params: queryToFetch })
      .then(response => response.data)
      .catch(apiErrorHandler);
    return { [key]: data.activities || [], totalCount: data.totalCount != null ? data.totalCount : 0 };
  };

  return useCustomPaginatedQuery(extAuthenticationId, getExtAuthenticationActivity, query, {
    shouldFetch: !!extAuthenticationId,
    ...options
  });
};

export const updateExtAuthentication = (extAuthenticationId, payload) => {
  return httpClient
    .patch(`/extAuthentication/${extAuthenticationId}`, payload)
    .then(response => response)
    .catch(apiErrorHandler);
};

export const deleteExtAuthentication = extAuthenticationId => {
  return httpClient
    .delete(`/extAuthentication/${extAuthenticationId}`)
    .then(response => response)
    .catch(apiErrorHandler);
};

/**
 * @param {object} payload
 * @param {string} payload.name
 * @param {object} payload.mapping
 * @param {('new'|'reuse')} payload.mapping.type
 * @param {string[]=} [payload.mapping.hostIds] - Required when type is 'new' (parent + child host ids)
 * @param {string=} [payload.mapping.extAuthenticationId] - Required when type is 'reuse'
 * @param {object} payload.propertyAllowlist
 * @param {('all'|'selected')=} [payload.propertyAllowlist.scope] - 'all' = every property in mapping scope; 'selected' = subset. Omit for legacy “all for mapped hosts”
 * @param {Array<{ hostId: string, externalId?: string, listingPropertyId?: string, id?: string }>=} [payload.propertyAllowlist.items] - Each item must include disambiguation: `externalId` and/or `listingPropertyId` and/or compound `id` (`hostId:listingPropertyId`); avoid `{ hostId }` only when multiple properties share the mapping
 */
export const createAccessToken = payload => {
  return httpClient
    .post('/extAuthentication/create-token', payload)
    .then(response => response.data)
    .catch(apiErrorHandler);
};

export const updateExtAuthenticationToken = extAuthenticationId => {
  return httpClient
    .post(`/extAuthentication/${extAuthenticationId}/update-token`)
    .then(response => response)
    .catch(apiErrorHandler);
};

export const addHostMappings = (extAuthenticationId, hostIds) => {
  return httpClient
    .post(`/extAuthentication/${extAuthenticationId}/host-mappings`, { hostIds })
    .then(response => response.data)
    .catch(apiErrorHandler);
};

export const getExtAccessHosts = (hostIds = [], getChildHost = false, shared = false) => {
  const ids = toIdArray(hostIds);
  const params = {
    hostIds: encodeURIComponent(JSON.stringify(ids)),
    getChildHost,
    shared
  };
  return httpClient
    .get('/extAccess/host', { params })
    .then(response => response.data)
    .catch(apiErrorHandler);
};

export const getExtAuthenticationOptions = () => {
  return httpClient
    .get('/extAuthentication/options')
    .then(response => response.data)
    .catch(apiErrorHandler);
};

/**
 * Trigger PMS sync for the given extAccess rows (PATCH /extAccess/sync).
 *
 * @param {string | string[]} extAccessIds
 * @param {Record<string, unknown>=} [options] - merged into PATCH body after `extAccessIds` (e.g. `extAuthenticationId`)
 */
export const syncExtAccess = (extAccessIds, options = {}) => {
  const ids = toIdArray(extAccessIds);
  return httpClient
    .patch('/extAccess/sync', { extAccessIds: ids, ...options })
    .then(response => response)
    .catch(apiErrorHandler);
};

/**
 * Sync all host mappings for a token `
 * but uses PATCH /extAccess/sync with both ids so the server can scope work to that extAuthentication.
 *
 * @param {string} extAuthenticationId
 * @param {string | string[]} extAccessIds - each mapping’s `extAccess._id` from token detail
 * @param {Record<string, unknown>=} [extraBody] - optional extra PATCH fields
 */
export const syncExtAccessForExtAuthentication = (extAuthenticationId, extAccessIds, extraBody = {}) => {
  return syncExtAccess(extAccessIds, {
    extAuthenticationId: String(extAuthenticationId),
    ...extraBody
  });
};

/**
 * PMS: list every property the token’s mapped hosts can expose. Used before the user picks what to add/sync.
 * Response: `{ properties: [ { id, name, externalId, hostId, alreadyOnToken? } ] }` (or `data.properties`).
 */
export const getPullNewPropertyCandidates = extAuthenticationId => {
  return httpClient
    .get(`/extAuthentication/${extAuthenticationId}/pull-new-properties/candidates`)
    .then(response => response.data)
    .catch(apiErrorHandler);
};

/**
 * PMS property list for **create-token** wizard (no extAuthentication id yet).
 * Same response shape as getPullNewPropertyCandidates (`{ properties: [...] }`).
 * @param {string[]} hostIds - parent + selected child host ids for the new mapping
 */
export const postCreateTokenPropertyCandidates = hostIds => {
  const ids = toIdArray(hostIds);
  return httpClient
    .post('/extAuthentication/create-token/property-candidates', { hostIds: ids })
    .then(response => response.data)
    .catch(apiErrorHandler);
};

/**
 * Run pull+sync for the selected allowlist (and extend extAuth with new picks). If body is empty/legacy, backend may
 * treat as “all candidates” (older behavior).
 * @param {string} extAuthenticationId
 * @param {{ propertyAllowlist: { items: Array<{ hostId: string, externalId?: string, listingPropertyId?: string, id?: string }> } }=} payload
 */
export const postPullNewPropertiesForExtAuthentication = (extAuthenticationId, payload) => {
  return httpClient
    .post(`/extAuthentication/${extAuthenticationId}/pull-new-properties`, payload != null ? payload : {})
    .then(response => response)
    .catch(apiErrorHandler);
};

/**
 * Unlink this extAuthentication from a shared extAccess. Does not delete extAccess (other tokens may use it).
 * Preferred route (token in path), not unscoped PATCH /extAccess/:id/host-mapping.
 */
export const removeHostMapping = (extAuthenticationId, extAccessId) => {
  return httpClient
    .patch(`/extAuthentication/${extAuthenticationId}/host-mappings/${extAccessId}`)
    .then(response => response)
    .catch(apiErrorHandler);
};

export const exportExtAccessesCsv = (extAuthenticationId, extAccessIds) => {
  const ids = toIdArray(extAccessIds);
  return httpClient
    .post('/extAccess/csv', { extAuthenticationId, extAccessIds: ids })
    .then(response => response.data)
    .catch(apiErrorHandler);
};
